// components/header/BottomHeaderItems.tsx
"use client";
import BottomHeaderItem from "./BottomHeaderItem";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { useCallback, useState } from "react";
import { Swiper as SwiperType } from "swiper";
import SwiperButton from "@/app/components/elements/buttons/SwiperButton";
import useMenus from "@/app/lib/features/menus/menus.hook";
import BottomHeaderItemsSkeleton from "@/app/components/elements/skeletons/BottomHeaderItemsSkeleton";

const BottomHeaderItems = () => {
  const { menus, isLoading } = useMenus();
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);

  const updateGradients = useCallback((swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  }, []);

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      setSwiperInstance(swiper);
      updateGradients(swiper);
    },
    [updateGradients],
  );

  // Skeleton: sadece aktif yükleniyorken VE henüz veri yoksa göster.
  // ISR cache'den gelen initialMenus zaten store'da — skeleton hiç görünmez.
  if (isLoading && !menus) {
    return <BottomHeaderItemsSkeleton />;
  }

  if (!menus || menus.length === 0) return null;

  return (
    <div className="relative group w-full min-w-0">
      <Swiper
        spaceBetween={24}
        freeMode={{
          enabled: true,
          momentumRatio: 0.8,
          momentumVelocityRatio: 0.8,
        }}
        touchReleaseOnEdges
        touchAngle={45}
        resistance={false}
        onSwiper={handleSwiper}
        onSlideChange={updateGradients}
        onProgress={updateGradients}
        onTouchEnd={(swiper) => setTimeout(() => updateGradients(swiper), 50)}
        onTransitionEnd={updateGradients}
        modules={[FreeMode]}
        slidesPerView="auto"
        className="bottom-header-swiper overflow-hidden! py-2.5!"
      >
        {menus.map((item, i) => (
          <SwiperSlide key={i} className="w-auto! shrink-0">
            <BottomHeaderItem item={item} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation Buttons */}
      <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-2 pointer-events-none hidden xl:flex opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="pointer-events-auto">
          <SwiperButton
            swiper={swiperInstance}
            position="left"
            slideMode="group"
            size="xs"
          />
        </div>
        <div className="pointer-events-auto">
          <SwiperButton
            swiper={swiperInstance}
            position="right"
            slideMode="group"
            size="xs"
          />
        </div>
      </div>

      {/* Gradient Overlays */}
      <div
        className={`absolute top-0 left-0 z-20 w-5 sm:w-8 lg:w-14 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none ${
          showLeftGradient ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`absolute top-0 right-0 z-20 w-5 sm:w-8 lg:w-14 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none ${
          showRightGradient ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

export default BottomHeaderItems;
